import CheckIcon from '@mui/icons-material/Check'
import InfoIconOutlined from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useCallback, useMemo, useState } from 'react'

import { affectedPetIds } from '@shared/availability'
import { capitalizeWords, formatListWithConjunction, formatNumberAsPrice, isEmpty } from '@shared/common'
import { pl, i18WithParams as t } from '@shared/locales'
import { BoardingDefaultImg } from 'asset/boarding_default_img'
import { DaycareDefaultImg } from 'asset/daycare_default_img'
import { PackagesDefaultImg } from 'asset/packages_default_img'
import { DiscountTag } from 'discount_tag/discount_tag'
import { SpecieAvatar } from 'icon_avatar/icon_avatar'
import {
  AvailabilityGroupClientDto,
  BookingSearchByDaysClientDto,
  BookingSearchClientDto,
} from 'service-api/src/types/api'
import { SimpleModal } from 'simple_modal/simple_modal'
import { TempTestTag } from 'temp_test_tag/temp_test_tag'
import { AssetImage } from '../asset_image/asset_image'
import { waitlistEligible } from '../shared/offers'
import { Spacer2 } from '../spacer/spacer'
import {
  getBaseGroup,
  getRoomPrice,
  getTemperTestBadgeText,
  isDaycareOfferAvailableWithTempTest,
  isDaycareOfferUnavailable,
  isDaycareOfferUnavailableWithTempTestFail,
  isHatchSourced,
} from './helper'
import type { TOfferCardProps } from './offer_card.d'
import { OfferIncludes } from './offer_includes'

export const OfferCard = ({
  searchResult,
  petGroup,
  onSelectOffer,
  showIncludes = true,
  serviceTypeName,
  specieTypeName,
  selected,
  totalPets,
  temperTest,
  onSelectScheduleTempTest,
  petNames,
  locationSpecies,
  InnerComponent,
  subscriberGroup,
}: TOfferCardProps) => {
  const [showTempTestModal, setShowTempTestModal] = useState(false)
  const isDayCare = serviceTypeName === 'daycare'
  const isPackages = serviceTypeName === 'packages'
  const availabilityGroups: AvailabilityGroupClientDto[] | AvailabilityGroupClientDto[][] | undefined = useMemo(
    () =>
      (searchResult as BookingSearchClientDto).availabilityGroups ||
      (searchResult as BookingSearchByDaysClientDto).availabilityGroupsByDays ||
      [],
    [searchResult]
  )
  const { offer } = searchResult
  const hasRequiredAddons = 'hasRequiredAddons' in searchResult && Boolean(searchResult.hasRequiredAddons)
  const hasWaitlistEnabled = subscriberGroup?.waitlistEnabled || false

  const group = useMemo(
    () => getBaseGroup(serviceTypeName, petGroup, availabilityGroups),
    [availabilityGroups, petGroup, serviceTypeName]
  )

  const temperTestBadgeText = getTemperTestBadgeText(availabilityGroups, isDayCare)

  const room = useMemo(() => group.availabilities?.[0] || {}, [group.availabilities])
  const hasImage = Boolean(offer?.assetUsages?.[0]?.asset)

  const isOfferAvailable = useMemo(
    () =>
      isDayCare
        ? !isDaycareOfferUnavailable(
            availabilityGroups as AvailabilityGroupClientDto[][] | undefined,
            hasWaitlistEnabled
          )
        : isPackages || !!room.available,
    [isDayCare, room.available, availabilityGroups, isPackages, hasWaitlistEnabled]
  )

  const isOfferAvailableWithTempTest = useMemo(
    () =>
      isDayCare
        ? isDaycareOfferAvailableWithTempTest(availabilityGroups as AvailabilityGroupClientDto[][] | undefined)
        : (room.unavailableReason?.type === 'TEMPER_TEST' && room.unavailableReason?.contingent) ||
          (room.condition?.reason?.type === 'TEMPER_TEST' && !isHatchSourced(room.unavailableReason)),
    [isDayCare, room, availabilityGroups]
  )

  const isOfferUnavailableWithTempTestFail = useMemo(
    () =>
      isDayCare
        ? isDaycareOfferUnavailableWithTempTestFail(availabilityGroups as AvailabilityGroupClientDto[][] | undefined)
        : room.unavailableReason?.type === 'TEMPER_TEST' && room.unavailableReason?.temperTestFailed,
    [isDayCare, room, availabilityGroups]
  )

  const canProceedWithOffer = (isOfferAvailable || isOfferAvailableWithTempTest) && !isOfferUnavailableWithTempTestFail
  const canBeOnWaitlist = waitlistEligible(group.availabilities)

  const packageAppliesTo = useMemo(() => {
    if (!isPackages) {
      return undefined
    }
    return offer?.locationSpecies ? [offer.locationSpecies] : locationSpecies
  }, [isPackages, locationSpecies, offer])

  const tempTestName = temperTest?.displayName || t('tempTest.default')

  const onSelectOfferClick = useCallback(
    () => onSelectOffer(offer!, group, petGroup, { hasRequiredAddons, type: 'booking' }),
    [hasRequiredAddons, onSelectOffer, offer, group, petGroup]
  )

  const onJoinWaitlistClick = useCallback(
    () => onSelectOffer(offer!, group, petGroup, { hasRequiredAddons, type: 'waitlist' }),
    [hasRequiredAddons, onSelectOffer, offer, group, petGroup]
  )

  const onScheduleTempTestClick = useCallback(() => {
    onSelectOffer(offer!, group, petGroup, { holdNavigation: true, type: 'booking' })
    onSelectScheduleTempTest(affectedPetIds(availabilityGroups))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer, group, onSelectOffer, petGroup])

  const renderIncludes = () => {
    return (
      <Box>
        <Spacer2 />
        <Typography component="h6">
          <strong>{t('global.label.includes')}:</strong>
        </Typography>
        <Spacer2 />
        {offer?.serviceOffers?.map((service) => (
          <OfferIncludes
            key={service.id!}
            service={service}
            locationPricingStrategy={group.pricingStrategy || 'DAILY'}
            showStrategy={false}
          />
        ))}
      </Box>
    )
  }

  const renderDefaultImage = () => {
    switch (serviceTypeName) {
      case 'daycare':
        return <DaycareDefaultImg />
      case 'packages':
        return <PackagesDefaultImg />
      default:
        return <BoardingDefaultImg />
    }
  }

  const strategyStr =
    group.pricingStrategy === 'NIGHTLY' ? t('global.label.strategy.night') : t('global.label.strategy.day')
  const price = isPackages ? offer?.prices?.[0]?.value : getRoomPrice(group, isDayCare, totalPets)
  const priceVaries = offer?.priceVaries

  const formattedPetNames = formatListWithConjunction(petNames)
  const formattedTemperTestPetNames = formatListWithConjunction(
    room.affectedByPets?.map(({ displayName }) => displayName ?? '')
  )

  return (
    <Box>
      <Stack sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography variant="h4" sx={{ marginRight: 2, marginBottom: { xs: 1, sm: 3 } }}>
          {offer?.displayName}
        </Typography>
        {temperTestBadgeText && <TempTestTag tempTestName={temperTestBadgeText} cssOverrides={{ mb: 1, mr: 1 }} />}
        <DiscountTag
          discounts={group?.availabilities?.[0]?.price?.discounts}
          cssOverrides={{ mb: 1 }}
          eligibleForConsumableBenefits={searchResult.eligibleForConsumableBenefits}
        />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4 }}>
        {hasImage ? <AssetImage asset={offer!.assetUsages![0].asset!} /> : renderDefaultImage()}
        <Stack direction="column" width="100%">
          <>
            <Typography variant="body1" color="text.secondary">
              {offer?.description}
            </Typography>
            {packageAppliesTo && (
              <Box>
                <Spacer2 />
                <Typography component="h6">
                  <strong>{t('global.label.appliesTo')}:</strong>
                </Typography>
                <Spacer2 />
                {packageAppliesTo.map((locationSpecie) => (
                  <Chip
                    key={locationSpecie.id}
                    sx={{
                      marginRight: 1,
                      marginBottom: 1,
                      '& svg': {
                        margin: '0 !important',
                      },
                      '& span': {
                        pl: '6px',
                      },
                    }}
                    avatar={
                      <SpecieAvatar
                        specie={locationSpecie.species?.name || ''}
                        size="small"
                        disabled={true}
                        reverseColors={true}
                      />
                    }
                    label={pl(
                      locationSpecie.displayName ||
                        locationSpecie.species?.displayName ||
                        locationSpecie.species?.name ||
                        '',
                      0
                    )}
                  />
                ))}
              </Box>
            )}
            {showIncludes && renderIncludes()}
            <Divider sx={{ my: 2 }} />
            <Stack
              direction={{
                xs: 'column',
                sm: 'column',
                md: 'row',
                lg: 'row',
                xl: 'row',
              }}
              spacing={3}
              justifyContent="end"
              alignItems="center"
            >
              {canProceedWithOffer ? (
                <>
                  {isEmpty(price) ? (
                    <Typography variant="body1">{t('global.lable.priceUnavailable')}</Typography>
                  ) : (
                    <Box>
                      <Typography variant="h5" data-testid="price-tag" textAlign="right">
                        <Typography variant="body1" component="span" sx={{ paddingRight: '4px' }}>
                          {priceVaries && !isDayCare && t('offers.startsAt')}
                        </Typography>
                        <strong>
                          {isDayCare && <span>{t('global.label.from')}&nbsp;</span>}
                          {formatNumberAsPrice(price)}
                        </strong>
                        {!isPackages && (
                          <Typography variant="body1" component="span">
                            /
                            {isDayCare && (
                              <span>
                                {specieTypeName} {t('global.label.per')}&nbsp;
                              </span>
                            )}
                            <span>{strategyStr}</span>
                          </Typography>
                        )}
                      </Typography>
                      {hasRequiredAddons && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <InfoIconOutlined fontSize="small" color="action" />
                          <Typography variant="caption" color="text.primary">
                            {t('offerCard.requiredAddons')}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  )}
                  {isOfferAvailableWithTempTest ? (
                    <Button
                      size="large"
                      variant={selected ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => setShowTempTestModal(true)}
                      startIcon={selected ? <CheckIcon /> : null}
                      sx={{
                        width: {
                          xs: '100%',
                          md: 'auto',
                        },
                      }}
                    >
                      {t('tempTestModal.Schedule', { temperTest: tempTestName })}
                    </Button>
                  ) : (
                    <Button
                      size="large"
                      variant={selected ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={onSelectOfferClick}
                      startIcon={selected ? <CheckIcon /> : null}
                      sx={{
                        width: {
                          xs: '100%',
                          md: 'auto',
                        },
                      }}
                    >
                      {t(selected ? 'global.label.selected' : 'global.label.select')}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {hasWaitlistEnabled && canBeOnWaitlist ? (
                    <>
                      <Typography>{t('offerCard.notAvailable.waitlist')}</Typography>
                      <Button
                        size="large"
                        variant={selected ? 'contained' : 'outlined'}
                        onClick={onJoinWaitlistClick}
                        color="neutral"
                        startIcon={selected ? <CheckIcon /> : null}
                        sx={{
                          borderRadius: '30px',
                          width: {
                            xs: '100%',
                            md: 'auto',
                          },
                        }}
                      >
                        {t('offerCard.waitlist')}
                      </Button>
                    </>
                  ) : (
                    <Typography>{t('offerCard.noAvailable.callToBook')}</Typography>
                  )}
                </>
              )}
            </Stack>
          </>
        </Stack>
      </Stack>
      {InnerComponent}
      <SimpleModal open={showTempTestModal} onClose={() => setShowTempTestModal(false)}>
        <Typography variant="h6">
          {t('tempTestModal.title', { temperTest: tempTestName, temperTestPets: formattedTemperTestPetNames })}
        </Typography>
        <Spacer2 />
        <Typography variant="body1">
          {t('tempTestModal.info', {
            temperTest: tempTestName,
            temperTestPets: formattedTemperTestPetNames,
            pets: formattedPetNames,
            serviceType: capitalizeWords(serviceTypeName.toLowerCase()),
          })}
        </Typography>
        <Spacer2 />
        <Typography variant="body1">{t('tempTestModal.info2', { serviceType: serviceTypeName })}</Typography>
        <Spacer2 />
        <Button
          size="large"
          variant="contained"
          onClick={onScheduleTempTestClick}
          sx={{
            width: {
              xs: '100%',
            },
          }}
        >
          {t('tempTestModal.button', { temperTest: tempTestName })}
        </Button>
        <Button
          size="large"
          variant="text"
          onClick={() => setShowTempTestModal(false)}
          sx={{
            width: {
              xs: '100%',
            },
          }}
        >
          {t('global.label.goBack')}
        </Button>
      </SimpleModal>
    </Box>
  )
}
